import { IconButton, Menu, MenuItem } from '@mui/material';
import { Print, ArrowDropDown } from '@mui/icons-material';
import { useAlert } from "components/UI/alertSakeBar/alertProvider";
import Loading from "components/Loading";
import React, { useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import styled from "styled-components";

const StyledTh = styled.th`
    background: #1e3a8a;
    color: #ffffff;
    padding: 12px 16px;
    border: 0.5px solid black;
    text-align: left;
    font-size: 14px;
    font-weight: 600;
    
    @media print {
        border: 0.5px solid black !important;
        background: #1e3a8a !important;
        color: #ffffff !important;
        -webkit-print-color-adjust: exact !important;
        color-adjust: exact !important;
        print-color-adjust: exact !important;
    }
`;

const StyledTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;

    td, th {
        padding: 12px 16px;
        word-wrap: break-word;
        white-space: normal;
        border: 0.5px solid black;
        color: #000000;
    }

    @media print {
        border-collapse: collapse !important;
        table-layout: fixed !important;

        td, th {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color: #000000 !important;
        }

        thead th {
            background: #1e3a8a !important;
            color: #ffffff !important;
        }

        thead {
            display: table-header-group;
        }
    }
`;

const StyledTableRow = styled.tr`
    page-break-inside: avoid;
    break-inside: avoid;
    
    @media print {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
    }
`;

const PrintStyles = styled.div`
    @media print {
        body, html {
            width: 100%;
            height: auto;
            overflow: visible !important;
        }

        .print-container {
            width: 100%;
            height: auto !important;
            overflow: visible !important;
            position: relative;
        }

        .printable {
            width: 100%;
            height: auto;
        }

        table {
            page-break-inside: auto;
        }
        
        tr {
            page-break-inside: avoid;
            break-inside: avoid;
            page-break-after: auto;
        }
        
        td, th {
            page-break-inside: avoid;
            break-inside: avoid;
        }

        table {
            border-collapse: collapse !important;
            border-spacing: 0 !important;
        }

        thead {
            display: table-header-group;
        }

        tfoot {
            display: table-footer-group;
        }
    }
`;

const PrintComponent = React.forwardRef(({ data }, ref) => {
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
        });
    };

    return (
        <PrintStyles>
            <div ref={ref} className="print-container printable">
                <style>{`
                    @page { 
                        size: letter landscape; 
                        margin: 15mm; 
                    }
                    
                    @media print {
                        tr {
                            page-break-inside: avoid !important;
                            break-inside: avoid !important;
                        }
                        
                        td, th {
                            page-break-inside: avoid !important;
                            break-inside: avoid !important;
                        }
                        
                        table {
                            page-break-inside: auto;
                        }
                        
                        tbody tr {
                            page-break-inside: avoid !important;
                            break-inside: avoid !important;
                        }
                    }
                `}</style>
                <div style={{ width: "100%", overflowX: "auto", padding: 20 }}>
                    <StyledTable>
                        <thead>
                            <tr>
                                <StyledTh style={{ width: '7%' }}>Seq No.</StyledTh>
                                <StyledTh style={{ width: '15%' }}>Teacher Name</StyledTh>
                                <StyledTh style={{ width: '20%' }}>School Name</StyledTh>
                                <StyledTh style={{ width: '15%' }}>Date</StyledTh>
                                <StyledTh style={{ width: '13%' }}>Status</StyledTh>
                                <StyledTh style={{ width: '27%' }}>Notes</StyledTh>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((element, index) => (
                                <StyledTableRow key={element.id || `${element.teacher}-${index}`}>
                                    <td style={{ textAlign: 'left' }}>{index + 1}</td>
                                    <td>{element.teacher || '-'}</td>
                                    <td>{element.district || '-'}</td>
                                    <td>{formatDate(element.date_time)}</td>
                                    <td>{element.status || '-'}</td>
                                    <td>{element.note || '-'}</td>
                                </StyledTableRow>
                            ))}
                        </tbody>
                    </StyledTable>
                </div>
            </div>
        </PrintStyles>
    );
});

const TeacherSupportPrint = ({ data }) => {
    const componentRef = useRef();
    const [printLoader, setPrintLoader] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const showAlert = useAlert();

    const handlePrint = useReactToPrint({
        contentRef: componentRef,
        documentTitle: "Teacher Support Records",
        pageStyle: "@page { size: letter landscape; margin: 15mm; }",
        onBeforeGetContent: async () => setPrintLoader(true),
        onBeforePrint: async () => setPrintLoader(false),
    });

    const handleDropdownOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleDropdownClose = () => {
        setAnchorEl(null);
    };

    const handlePrintOption = () => {
        handleDropdownClose();
        if (!data || data.length === 0) {
            return showAlert("No teacher support records to print!", "error");
        }
        handlePrint();
    };

    return (
        <div className="w-fit-content">
            <IconButton 
                style={{ padding: 0 }} 
                aria-label="print" 
                onClick={handleDropdownOpen}
            >
                {printLoader ? (
                    <Loading size={20} />
                ) : (
                    <>
                        <Print style={{ color: "white", fontSize: 24 }} />
                        <ArrowDropDown style={{ color: "white", fontSize: 16 }} />
                    </>
                )}
            </IconButton>
            
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleDropdownClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem onClick={handlePrintOption}>
                    Print Records
                </MenuItem>
            </Menu>

            <div style={{ display: "none" }} className="printable">
                <PrintComponent ref={componentRef} data={data} />
            </div>
        </div>
    );
};

export default TeacherSupportPrint;

